import {
  EnvelopeIcon,
  FolderOpenIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../components/shared/card";

import { ContactRow } from "../../api/relationship.type";
import { GroupRow } from "../../api/group.type";
import Typography from "../../components/shared/typography";
import { useContactsContext } from "../../hooks/ContactsContextWrapper";
import { SubmitButton } from "../shared/buttons/submit-button";
import { ConfirmationModal } from "../shared/ConfirmationModal";
import { formatPhoneNumberToHumanReadable } from "../utls/utils";
import ContactItemGroups from "./contact-item-groups";
import { RelationshipIcon } from "./relationship-icon";

export const ContactItem = ({
  contact,
  groups,
  onAddToGroup,
  onRemoveFromGroup,
}: {
  contact: ContactRow;
  groups: GroupRow[];
  onAddToGroup: (groupId: string) => Promise<void> | void;
  onRemoveFromGroup: (groupId: string) => Promise<void> | void;
}) => {
  const navigate = useNavigate();
  const { remove } = useContactsContext();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleInviteNav = () => {
    navigate(`/contacts/invite/${contact.id}`, {
      state: {
        contact,
      },
    });
  };

  const navigateToView = () => navigate(`/contacts/${contact.id}`);

  return (
    <Card
      key={contact.id}
      className="w-full cursor-pointer"
      variant="default"
      elevation={1}
      onClick={navigateToView}
    >
      <Card.Body className="relative grid grid-cols-3 gap-2 p-4 m-2">
        <SubmitButton
          size="sm"
          iconOnly
          color="cream"
          className="absolute top-1 right-1"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/contacts/update/${contact.id}`);
          }}
          icon={<PencilSquareIcon className="w-4 h-4" />}
        />
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <Typography variant="subtitle1" className="text-terracotta">
              {contact.related_user.first_name} {contact.related_user.last_name}
            </Typography>
            <RelationshipIcon
              role={contact.related_user_role}
              className="w-4 h-4 text-primary-600"
            />
          </div>
          <Typography variant="caption" className="text-primary-600">
            {contact.related_user.email}
          </Typography>
          <Typography variant="caption" className="text-primary-600">
            {contact.related_user.phone
              ? formatPhoneNumberToHumanReadable(contact.related_user.phone)
              : ""}
          </Typography>
        </div>
        <ContactItemGroups
          groups={groups}
          contact={contact}
          onAddToGroup={onAddToGroup}
          onRemoveFromGroup={onRemoveFromGroup}
        />

        <div className="flex flex-col  gap-2 w-40">
          <SubmitButton
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleInviteNav();
            }}
            color="beige"
            icon={<EnvelopeIcon className="w-4 h-4" />}
          >
            Invite User
          </SubmitButton>
          {contact.record_view && (
            <SubmitButton
              size="sm"
              color="cream"
              icon={<FolderOpenIcon className="w-4 h-4" />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/medical-records/user/${contact.related_user_id}`);
              }}
            >
              Medical Records
            </SubmitButton>
          )}
        </div>
      </Card.Body>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => remove(contact.id)}
        title="Delete Contact"
        message={`Are you sure you want to delete ${contact.related_user.first_name} ${contact.related_user.last_name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        tone="danger"
      />
    </Card>
  );
};
